import { useMemo, useState } from "react";
import { Input, Table, Button } from "antd";
import { BiSearchAlt } from "react-icons/bi";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import ExportExcel from "components/excel/ExportExcel";
import Loader from "components/loader";
import { routesNames } from "utils/Constants";
import { getPublicationsByState, getPostulationsByPublication } from "services/Publications";

const MIN_KEYWORD_LENGTH = 3;
const MAX_MATCHED_PUBLICATIONS = 60;

// Misma lógica que se usa en el listado de Publicaciones para determinar el
// estado real de una publicación (vencida / desactivada / el estado que tenga cargado).
const getEstadoPublicacion = (pub) => {
    const vencimiento = new Date(pub.vencimiento);
    const hoy = new Date();

    if (hoy > vencimiento) return "Vencida";
    if (pub.activa == 0) return "Desactivada";
    if (pub.estado_publicacion) return pub.estado_publicacion.nombre;
    return "";
};

const PostulationsSearchReport = () => {
    const [keyword, setKeyword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [searchedFor, setSearchedFor] = useState(null);
    const [matchedCount, setMatchedCount] = useState(0);
    const [results, setResults] = useState([]);

    const columns = [
        {
            title: "Postulante",
            dataIndex: "postulante",
            render: (text, record) => (
                <Link to={`${routesNames.USER}/${record.user_id}`} className="text-sm font-semibold text-blue-800 dark:text-white">
                    {record.postulante}
                </Link>
            ),
        },
        {
            title: "Fecha de postulación",
            dataIndex: "fecha",
            sorter: (a, b) => new Date(a.fechaRaw) - new Date(b.fechaRaw),
            defaultSortOrder: "descend",
        },
        {
            title: "Publicación",
            dataIndex: "publicacion",
            render: (text, record) => (
                <Link to={`${routesNames.PUBLICATION}/${record.publicacion_id}`} className="text-sm font-semibold text-blue-800 dark:text-white">
                    {record.publicacion}
                </Link>
            ),
        },
        {
            title: "Estado de la publicación",
            dataIndex: "estado",
        },
        {
            title: "Institución",
            dataIndex: "institucion",
            render: (text, record) => (
                record.institucion_user_id ? (
                    <Link to={`${routesNames.USER}/${record.institucion_user_id}`} className="text-sm font-semibold text-blue-800 dark:text-white">
                        {record.institucion}
                    </Link>
                ) : (
                    record.institucion
                )
            ),
        },
    ];

    const handleSearch = async () => {
        const term = keyword.trim();
        if (term.length < MIN_KEYWORD_LENGTH) return;

        setIsLoading(true);
        setSearchedFor(term);
        setResults([]);
        setMatchedCount(0);

        try {
            const publicacionesResponse = await getPublicationsByState({});
            const allPublications = Object.values(publicacionesResponse?.data || {}).filter((item) => item?.created_at);

            const matched = allPublications
                .filter((pub) => pub.nombre?.toLowerCase().includes(term.toLowerCase()))
                .slice(0, MAX_MATCHED_PUBLICATIONS);

            setMatchedCount(matched.length);

            const postuladosPerPublication = await Promise.all(
                matched.map(async (pub) => {
                    const raw = await getPostulationsByPublication(pub.id);
                    const postulaciones = raw?.[0]?.postulaciones || [];

                    return postulaciones.map((item) => ({
                        key: `${pub.id}-${item.id}`,
                        user_id: item.user_id,
                        postulante: `${item.postulante?.nombre || ""} ${item.postulante?.apellido || ""}`.trim(),
                        fecha: item.created_at ? dayjs(item.created_at).format("DD/MM/YYYY") : "—",
                        fechaRaw: item.created_at,
                        publicacion_id: pub.id,
                        publicacion: pub.nombre,
                        estado: getEstadoPublicacion(pub),
                        institucion: pub.cuenta?.nombre,
                        institucion_user_id: pub.cuenta?.user_id,
                    }));
                })
            );

            setResults(postuladosPerPublication.flat());
        } finally {
            setIsLoading(false);
        }
    };

    const dataExcel = useMemo(
        () =>
            results.map((row) => ({
                Postulante: row.postulante,
                "Fecha de postulación": row.fecha,
                Publicación: row.publicacion,
                "Estado de la publicación": row.estado,
                Institución: row.institucion,
            })),
        [results]
    );

    return (
        <div className="flex flex-col gap-5">
            <h1 className="mb-2 text-3xl font-medium">/ Postulaciones</h1>

            <p className="text-sm text-gray-600 dark:text-gray-400">
                Buscá una palabra o cargo (por ejemplo "preceptor") y vas a obtener todas las postulaciones a publicaciones
                cuyo título contenga esa palabra, sin importar cuándo se hayan hecho.
            </p>

            <div className="flex items-center gap-2">
                <Input
                    allowClear
                    placeholder="Buscar por palabra o cargo de la publicación (mínimo 3 letras)"
                    prefix={<BiSearchAlt className="text-xl" />}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onPressEnter={handleSearch}
                    className="max-w-md rounded-lg p-2"
                />
                <Button
                    type="primary"
                    onClick={handleSearch}
                    disabled={keyword.trim().length < MIN_KEYWORD_LENGTH}
                    className="bg-main"
                >
                    Buscar
                </Button>
            </div>

            {isLoading && <Loader />}

            {!isLoading && searchedFor && (
                <>
                    <div className="flex flex-row items-center justify-between">
                        <span className="block text-sm font-semibold">
                            {matchedCount} publicaciones con "{searchedFor}" en el título — {results.length} postulaciones encontradas
                        </span>
                        {results.length > 0 && (
                            <ExportExcel data={dataExcel} fileName={"Postulaciones_" + searchedFor + "_" + new Date().toLocaleDateString()} title="Descargar Excel" />
                        )}
                    </div>

                    <Table
                        columns={columns}
                        dataSource={results}
                        rowKey="key"
                        size="small"
                        pagination={{ pageSize: 20, showSizeChanger: false }}
                        locale={{ emptyText: "No se encontraron postulaciones para esa búsqueda" }}
                    />
                </>
            )}
        </div>
    );
};

export default PostulationsSearchReport;
